import React from "react";
import ExportButtons from "./ExportButtons";

const MonthlyReportTableModal = ({ data, onClose, orderTotals = {}, sizeSummary = [] }) => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  const filtered = (data || []).filter(row => {
    const rowDate = new Date(row.date);
    return rowDate.getMonth() === currentMonth && rowDate.getFullYear() === currentYear;
  });

  const grouped = {};
  const completedByOrder = {};

  filtered.forEach(entry => {
    const order = entry.cut_order_no || `Order-${entry.order_id}`;
    const color = entry.suborder_colour_name || `Color-${entry.sub_order_id}`;

    if (!grouped[order]) grouped[order] = {};
    if (!grouped[order][color]) grouped[order][color] = [];

    grouped[order][color].push(entry);

    if (!completedByOrder[order]) completedByOrder[order] = {};
    completedByOrder[order][color] = (completedByOrder[order][color] || 0) + entry.total_piece;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white w-full h-full p-6 overflow-y-auto relative rounded-none shadow-none">
        <button
          className="absolute top-4 right-6 text-2xl text-gray-500 hover:text-red-500 z-50"
          onClick={onClose}
        >
          ✖
        </button>

        <div className="flex items-center justify-between mb-6 pr-10">
          <div>
            <h2 className="text-3xl font-bold text-indigo-800">🧵 Monthly Ready to Sewing - Report</h2>
            <p className="text-sm text-gray-700 mt-1">
              📅 Month: <span className="font-semibold">{today.toLocaleString("default", { month: "long", year: "numeric" })}</span>
            </p>
          </div>
          <ExportButtons data={filtered} filename="Monthly_RTS_Report" elementId="printable-monthly-rts-table" />
        </div>

        <div id="printable-monthly-rts-table" className="px-4">
          {Object.entries(orderTotals).map(([order, plannedColors]) => {
            const actualColors = grouped[order] || {};
            const allColors = new Set([
              ...Object.keys(plannedColors || {}),
              ...Object.keys(actualColors || {})
            ]);

            const totalPlanned = Object.values(plannedColors).reduce((sum, val) => sum + val, 0);
            const totalCompleted = Object.entries(plannedColors).reduce(
              (sum, [color, planned]) => sum + (completedByOrder[order]?.[color] || 0),
              0
            );
            const totalPending = Math.max(totalPlanned - totalCompleted, 0);

            return (
              <div key={order} className="mb-10 border-t border-gray-300 pt-4">
                <h3 className="text-lg font-bold text-white bg-indigo-600 px-4 py-2 rounded-md shadow-md">
                  Order No: {order}
                </h3>

                <div className="text-sm text-gray-700 font-medium px-4 py-2 mb-2">
                  📦 Order Quantity: <strong>{totalPlanned}</strong> &nbsp; | &nbsp;
                  ✅ Completed: <strong>{totalCompleted}</strong> &nbsp; | &nbsp;
                  ⏳ Pending: <strong>{totalPending}</strong>
                </div>

                {[...allColors].map(color => {
                  const entries = actualColors[color] || [];
                  const completed = completedByOrder[order]?.[color] || 0;
                  const planned = plannedColors?.[color] || 0;
                  const pending = Math.max(planned - completed, 0);

                  const summaryMatch = sizeSummary.find(
                    s => s.order_no === order && s.suborder_colour_name === color
                  );

                  return (
                    <div key={color} className="mb-6 border border-gray-200 rounded-lg shadow bg-white">
                      <div className="bg-indigo-100 px-4 py-2 rounded-t-md flex flex-wrap justify-between">
                        <div className="text-sm font-semibold text-indigo-900">
                          🎨 Color:{" "}
                          <span className="bg-white px-2 py-1 rounded text-indigo-700 font-medium">
                            {color}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 flex gap-4">
                          <span>✅ Completed: <strong>{completed}</strong></span>
                          <span>📦 Order Quantity: <strong>{planned}</strong></span>
                          <span>⏳ Pending: <strong>{pending}</strong></span>
                        </div>
                      </div>

                      <table className="w-full text-sm">
                        <thead className="bg-indigo-200 text-indigo-900 font-semibold">
                          <tr>
                            <th className="px-4 py-2 border">Job No</th>
                            <th className="px-4 py-2 border">Total Pieces</th>
                            <th className="px-4 py-2 border">Sizes</th>
                            <th className="px-4 py-2 border">Remark</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.length > 0 ? (
                            entries.map((row, i) => (
                              <tr key={i} className="hover:bg-indigo-50 transition-all">
                                <td className="px-4 py-2 border text-gray-800">{row.job_no}</td>
                                <td className="px-4 py-2 border text-center font-bold text-green-700">
                                  {row.total_piece}
                                </td>
                                <td className="px-4 py-2 border text-gray-700">
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(row.size_variant_quantity || {}).map(([size, qty]) => (
                                      <span
                                        key={size}
                                        className="bg-yellow-100 text-gray-800 text-xs px-2 py-1 rounded shadow-sm"
                                      >
                                        {size}: {qty}
                                      </span>
                                    ))}
                                  </div>
                                </td>
                                <td className="px-4 py-2 border text-gray-600 italic">{row.remark || "-"}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td className="px-4 py-2 border text-gray-500 italic">-</td>
                              <td className="px-4 py-2 border text-center text-green-700 font-bold">0</td>
                              <td className="px-4 py-2 border text-gray-700">
                                <div className="flex flex-wrap gap-2">
                                  {summaryMatch ? (
                                    Object.entries(summaryMatch.size_variant_quantity || {}).map(([size, qty]) => (
                                      <span
                                        key={size}
                                        className="bg-yellow-100 text-gray-800 text-xs px-2 py-1 rounded shadow-sm"
                                      >
                                        {size}: {qty}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-gray-400 text-xs">No size info</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2 border text-gray-500 italic">
                                No entry found. This color is fully pending.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <p className="text-center text-gray-500 mt-6 italic">
              No Ready to Sewing data available for this month.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyReportTableModal;
